package domain.cards

import domain.levels.RoomObjective


enum class CardCategory {
    GRAVITY,
    PIECES,
    TIME,
    GARBAGE,
    BOARD,
    OBJECTIVE,
    MODIFIER,
}

sealed class CardEffect {

    // ── Gravity / timing ──────────────────────────────────────────────────────
    data class SetGravityMultiplier(val multiplier: Double) : CardEffect()
    data class SetLockDelay(val ms: Double) : CardEffect()
    data class SetMaxLockResets(val count: Int) : CardEffect()
    data class SetStartingLevel(val level: Int) : CardEffect()

    // ── Piece set ─────────────────────────────────────────────────────────────
    data class RestrictPieces(val allowedIds: List<Int>) : CardEffect()
    data class SetSpinMode(val mode: String) : CardEffect()
    data class SetBagType(val bag: String) : CardEffect()
    data class SetPreviewSize(val count: Int) : CardEffect()
    data class SetHoldEnabled(val enabled: Boolean) : CardEffect()
    data class SetCustomPieceSet(
        val pieceSetKey: String,
        val bagType: String = "SEVEN",
    ) : CardEffect()

    /**
     * Reverts the piece set back to standard tetrominoes.
     * This is the only way to "undo" a piece-set card mid-run.
     */
    object RestoreTetrominoes : CardEffect()

    // ── Time modifiers ────────────────────────────────────────────────────────
    data class ApplyTimeState(
        val state: String,
        val durationMs: Double?,
        val multiplier: Double?,
    ) : CardEffect()

    // ── Garbage ───────────────────────────────────────────────────────────────
    data class SetGarbageMessiness(val messiness: Int) : CardEffect()
    data class SetGarbageCap(val cap: Int) : CardEffect()
    data class AddGarbageLines(val lines: Int) : CardEffect()
    data class SetGarbageBlockId(val id: Int) : CardEffect()

    /**
     * Spawns blocked garbage rows at round start (Tetris Friends rules).
     * The hole column shifts every [columnShiftInterval] clears.
     */
    data class SpawnBlockedGarbage(
        val initialLines: Int,
        val columnShiftInterval: Int = 4,
    ) : CardEffect()

    /**
     * Queues periodic garbage attacks that arrive every [intervalLines] lines
     * cleared by the player.  Each attack adds [linesPerAttack] pending garbage.
     */
    data class SetPeriodicGarbage(
        val linesPerAttack: Int,
        val intervalLines: Int,
    ) : CardEffect()

    /**
     * When active, line clears cancel pending incoming garbage instead of
     * (or in addition to) advancing the objective.
     */
    data class SetGarbageFireback(
        val singleCancel:  Int = 0,
        val doubleCancel:  Int = 1,
        val tripleCancel:  Int = 2,
        val gravitarCancel: Int = 4,
        val spinBonus:     Int = 1,
    ) : CardEffect()

    /**
     * Garbage-objective cards: the room objective is only considered met when
     * BOTH the primary objective is satisfied AND the board contains zero
     * garbage rows (block ID -99).
     */
    object RequireGarbageClear : CardEffect()

    // ── Score / coins ─────────────────────────────────────────────────────────
    data class SetScoreMultiplier(val multiplier: Double) : CardEffect()
    data class SetCoinMultiplier(val multiplier: Double) : CardEffect()

    // ── Room objective ────────────────────────────────────────────────────────
    /**
     * Overrides the room objective. Does NOT set multipliers — those are separate
     * effects so they compose freely with any bonus card.
     */
    data class SetRoomObjective(val objective: RoomObjective) : CardEffect()

    // ── Board behaviour ───────────────────────────────────────────────────────
    data class FreezeBoardMode(val nextLevelLines: Int = 40) : CardEffect()
}

enum class CardRarity { COMMON, RARE, EPIC, LEGENDARY }

enum class CardTarget { SELF, OPPONENT, MATCH, BOTH }

/**
 * Scaling envelope for a numeric value inside a card.
 *
 *   value(level) = base + perRoom * (level - 1),  clamped to [min, max]
 */
data class ScaleEnvelope(
    val base: Double,
    val perRoom: Double,
    val min: Double = 0.0,
    val max: Double = Double.MAX_VALUE,
) {
    fun at(level: Int): Double =
        (base + perRoom * (level - 1).coerceAtLeast(0)).coerceIn(min, max)
}

/**
 * Raw card template loaded from JSON (or declared inline in [CardRepository]).
 */
data class CardTemplate(
    val id: String,
    val name: String,
    /** Description template. Use "{OBJ}" as a placeholder for the scaled objective value. */
    val description: String,
    val flavourText: String?,
    val category: CardCategory,
    val rarity: CardRarity,
    val target: CardTarget,
    val effects: List<CardEffect>,
    val iconKey: String,
    val durationRounds: Int?,
    val coinCost: Int = rarity.defaultCost(),
    val unique: Boolean = false,
    /** Scaling envelope for the room objective target (lines, score, spins…). */
    val scalingObjective: ScaleEnvelope? = null,
    /** Scaling envelope for initial garbage lines spawned at room start. */
    val scalingGarbage: ScaleEnvelope? = null,
)

data class CardDefinition(
    val id: String,
    val name: String,
    val description: String,
    val flavourText: String?,
    val category: CardCategory,
    val rarity: CardRarity,
    val target: CardTarget,
    val effects: List<CardEffect>,
    val iconKey: String,
    val durationRounds: Int?,
    val coinCost: Int = rarity.defaultCost(),
    /** Whether this card is one-of-a-kind in the active deck. */
    val unique: Boolean = false,
)

/** Default coin cost derived from rarity. */
fun CardRarity.defaultCost(): Int = when (this) {
    CardRarity.COMMON    -> 2   // always affordable after one GRAVITAR clear
    CardRarity.RARE      -> 5
    CardRarity.EPIC      -> 9
    CardRarity.LEGENDARY -> 15
}

data class CardCollection(
    val version: String,
    val cards: List<CardDefinition>,
)
